import { useState, useEffect } from "react";
import { useConnectionStore } from "@/store/connection.store";

export default function useNavigatorOnline() {
  const supported =
    typeof window !== "undefined" &&
    typeof navigator !== "undefined" &&
    "onLine" in navigator;

  // Flag global que el interceptor activa cuando detecta un fallo de red real
  const detectedOffline = useConnectionStore((s) => s.detectedOffline);

  const [status, setStatus] = useState({
    previous: supported ? navigator.onLine : false,
    current: supported ? navigator.onLine : false,
  });

  // Eventos nativos del browser (DevTools offline / interfaz de red deshabilitada)
  useEffect(() => {
    if (!supported) return;

    const goOnline = () => {
      useConnectionStore.getState().setDetectedOffline(false);
      setStatus((prev) => ({
        previous: prev.current,
        current: true,
      }));
    };

    const goOffline = () => {
      setStatus((prev) => ({
        previous: prev.current,
        current: false,
      }));
    };

    const handleVisibilityOrFocus = () => {
      if (!(document.visibilityState === "visible" || document.hasFocus())) {
        return;
      }
      // Solo degradar a offline. Nunca forzar online por focus.
      if (!navigator.onLine) {
        goOffline();
      }
    };

    window.addEventListener("offline", goOffline);
    window.addEventListener("online", goOnline);
    window.addEventListener("focus", handleVisibilityOrFocus);
    document.addEventListener("visibilitychange", handleVisibilityOrFocus);

    return () => {
      window.removeEventListener("offline", goOffline);
      window.removeEventListener("online", goOnline);
      window.removeEventListener("focus", handleVisibilityOrFocus);
      document.removeEventListener("visibilitychange", handleVisibilityOrFocus);
    };
  }, [supported]);

  useEffect(() => {
    if (detectedOffline) {
      setStatus((prev) => {
        if (!prev.current) return prev;
        return { previous: prev.current, current: false };
      });
      return;
    }

    if (typeof navigator !== "undefined" && navigator.onLine) {
      setStatus((prev) => {
        if (prev.current) return prev;
        return { previous: prev.current, current: true };
      });
    }
  }, [detectedOffline]);

  const isOnline = status.current && !detectedOffline;

  return {
    backOnline: !status.previous && isOnline,
    backOffline: status.previous && !isOnline,
    isOnline,
    isOffline: !isOnline,
  };
}
